import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ImageWithFallback } from '../ui/ImageWithFallback';
import { Sparkles, Search, Command, ShoppingCart, Info, Activity, ArrowRight, X } from 'lucide-react';
import { useInstruments, useCart, useCurrency } from '../../contexts/SiteContexts';
import { toast } from 'react-hot-toast';
import { cn } from '../../lib/utils';

export const AISearch: React.FC = () => {
  const [query, setQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [results, setResults] = useState<any[]>([]);
  const [aiInsight, setAiInsight] = useState<string | null>(null);
  
  const { instruments } = useInstruments();
  const { addToCart } = useCart();
  const { formatPrice } = useCurrency();

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!query.trim()) return;

    setIsSearching(true);
    try {
      const response = await fetch('/api/intelligence/search', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query })
      });
      const data = await response.json();
      
      if (data.success) {
        const matched = data.result.relevantIds
          .map((id: string) => instruments.find(i => i.id === id))
          .filter(Boolean);
        
        setResults(matched);
        setAiInsight(data.result.insight);
      } else {
        throw new Error(data.error);
      }
    } catch (err) {
      console.error(err);
      toast.error("AI Search is temporarily unavailable. Falling back to local search.");
      // Fallback local search
      const localResults = instruments.filter(i => 
        i.name.toLowerCase().includes(query.toLowerCase()) || 
        i.category.toLowerCase().includes(query.toLowerCase()) ||
        i.description?.toLowerCase().includes(query.toLowerCase())
      ).slice(0, 8);
      setResults(localResults);
      setAiInsight("Displaying results based on local keyword matching.");
    } finally {
      setIsSearching(false);
    }
  };

  return (
    <div className="w-full max-w-4xl mx-auto mb-12">
      <div className="bg-white rounded-[2.5rem] border border-slate-100 shadow-2xl shadow-indigo-100/50 overflow-hidden">
        {/* Header Area */}
        <div className="p-8 bg-slate-50 border-b border-slate-100">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-indigo-600 rounded-xl flex items-center justify-center text-white shadow-lg shadow-indigo-200">
              <Sparkles size={20} />
            </div>
            <div>
              <h3 className="text-xl font-black text-slate-900 uppercase tracking-tight">AI Clinical Search</h3>
              <p className="text-[10px] font-black text-indigo-600 uppercase tracking-widest">Natural Language Inventory Analysis</p>
            </div>
          </div>
          
          <form onSubmit={handleSearch} className="relative group">
            <Search className="absolute left-6 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-indigo-600 transition-colors" size={20} />
            <input 
              type="text" 
              placeholder="e.g. 'Instruments for laparoscopic gallbladder surgery' or 'High-precision bone cutters'"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="w-full bg-white border border-slate-200 rounded-2xl py-5 pl-16 pr-24 text-sm font-bold text-slate-800 outline-none focus:ring-4 focus:ring-indigo-100 transition-all shadow-sm"
            />
            <button 
              type="submit"
              disabled={isSearching}
              className="absolute right-3 top-1/2 -translate-y-1/2 px-6 py-3 bg-slate-900 text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-indigo-600 transition-all disabled:opacity-50"
            >
              {isSearching ? 'Analyzing...' : 'Search'}
            </button>
          </form>
          
          <div className="mt-4 flex flex-wrap gap-2">
            <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest mr-2">Suggestions:</span>
            {['Urology Sets', 'Sterile Forceps', 'Bone Chisels', 'Laparoscopic Graspers'].map(suggestion => (
              <button 
                key={suggestion}
                onClick={() => {
                  setQuery(suggestion);
                  // Trigger search immediately if we wanted to
                }}
                className="px-3 py-1 bg-white border border-slate-200 rounded-full text-[9px] font-black text-slate-500 uppercase tracking-widest hover:border-indigo-400 hover:text-indigo-600 transition-all"
              >
                {suggestion}
              </button>
            ))}
          </div>
        </div>

        {/* Results Area */}
        <div className="p-8 min-h-[100px]">
          <AnimatePresence mode="wait">
            {isSearching ? (
              <motion.div 
                key="loading"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="py-20 flex flex-col items-center justify-center text-center"
              >
                <div className="w-16 h-16 border-4 border-indigo-100 border-t-indigo-600 rounded-full animate-spin mb-4" />
                <p className="text-xs font-black text-slate-500 uppercase tracking-widest animate-pulse">Gemini is analyzing your clinical requirements...</p>
              </motion.div>
            ) : results.length > 0 ? (
              <motion.div 
                key="results"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-6"
              >
                {aiInsight && (
                  <div className="bg-indigo-50 border border-indigo-100 p-5 rounded-2xl flex items-start gap-4">
                    <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center text-indigo-600 border border-indigo-200 shadow-sm flex-shrink-0">
                      <Info size={16} />
                    </div>
                    <div>
                      <p className="text-[9px] font-black text-indigo-600 uppercase tracking-widest mb-1">AI Clinical Insight</p>
                      <p className="text-xs font-bold text-indigo-900 leading-relaxed">{aiInsight}</p>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {results.map((item, idx) => (
                    <motion.div 
                      key={item.id}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: idx * 0.05 }}
                      className="group bg-slate-50 hover:bg-white border border-slate-100 hover:border-indigo-200 p-4 rounded-2xl flex items-center gap-4 transition-all hover:shadow-xl hover:shadow-indigo-50"
                    >
                      <div className="w-16 h-16 bg-white rounded-xl overflow-hidden border border-slate-100 group-hover:scale-110 transition-transform">
                        <ImageWithFallback src={item.imageUrl} alt={item.name} wrapperClassName="w-full h-full" className="w-full h-full object-cover" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-xs font-black text-slate-900 uppercase tracking-tight truncate">{item.name}</h4>
                        <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">{item.category}</p>
                        <p className="text-sm font-black text-indigo-600 mt-1">{formatPrice(item.price)}</p>
                      </div>
                      <button 
                        onClick={() => {
                          addToCart(item);
                          toast.success('Added to Cart');
                        }}
                        className="w-10 h-10 bg-white text-slate-400 hover:bg-indigo-600 hover:text-white rounded-xl border border-slate-200 hover:border-indigo-600 transition-all flex items-center justify-center"
                      >
                        <ShoppingCart size={16} />
                      </button>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            ) : results.length === 0 && !isSearching && query ? (
              <motion.div 
                key="empty"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="py-12 text-center"
              >
                <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <X className="text-slate-300" size={24} />
                </div>
                <p className="text-xs font-black text-slate-500 uppercase tracking-widest">No matching instruments found</p>
                <button 
                  onClick={() => setQuery('')}
                  className="mt-4 text-indigo-600 text-[10px] font-black uppercase tracking-widest hover:underline"
                >
                  Reset Search
                </button>
              </motion.div>
            ) : (
              <div className="py-12 flex flex-col items-center justify-center text-center opacity-40">
                <Command className="text-slate-300 mb-4" size={48} />
                <p className="text-xs font-black text-slate-400 uppercase tracking-widest">Query the Global Surgical Catalog</p>
              </div>
            )}
          </AnimatePresence>
        </div>

        {/* Footer Area */}
        <div className="px-8 py-4 bg-slate-50 border-t border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Activity size={14} className="text-indigo-400" />
            <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">AI Engine: Gemini Pro Flash 3.6</span>
          </div>
          <div className="flex items-center gap-1.5 text-indigo-600 group cursor-pointer">
            <span className="text-[9px] font-black uppercase tracking-widest">Advanced Filters</span>
            <ArrowRight size={12} className="group-hover:translate-x-1 transition-transform" />
          </div>
        </div>
      </div>
    </div>
  );
};
